import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { reportError } from '../utils/frontend-logger';

interface ErrorBoundaryProps {
    children: React.ReactNode;
    fallbackMessage?: string;
    resetKey?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Report to backend observability
        reportError(error, {
            source: 'ErrorBoundary',
            componentStack: errorInfo.componentStack?.substring(0, 500),
        });

        // ── Stale-chunk auto-reload ──
        const isStaleChunk =
            error.message?.includes('is not a valid JavaScript MIME type') ||
            error.message?.includes('Failed to fetch dynamically imported module') ||
            error.message?.includes('Importing a module script failed') ||
            error.message?.includes('error loading dynamically imported module');

        if (isStaleChunk) {
            const reloadKey = 'ragnarops_chunk_reload';
            const lastReload = sessionStorage.getItem(reloadKey);
            const now = Date.now();
            if (!lastReload || now - Number(lastReload) > 10_000) {
                sessionStorage.setItem(reloadKey, String(now));
                // Cache-bust: agregar ?cb=timestamp al URL para forzar al
                // browser/CDN a pedir un index.html fresh. location.reload()
                // a veces sirve la versión vieja desde memoria/proxy.
                const url = new URL(window.location.href);
                url.searchParams.set('cb', String(now));
                window.location.href = url.toString();
                return;
            }
        }
    }

    /**
     * Reset error state when the resetKey prop changes.
     * This ensures that navigating to a different module clears any
     * previous error, preventing the "stuck dark screen" bug.
     */
    componentDidUpdate(prevProps: ErrorBoundaryProps) {
        if (
            this.state.hasError &&
            prevProps.resetKey !== this.props.resetKey
        ) {
            this.setState({ hasError: false, error: null });
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 40px',
                    minHeight: '400px',
                }}>
                    <div style={{
                        background: 'rgba(239, 68, 68, 0.08)',
                        border: '1px solid rgba(239, 68, 68, 0.25)',
                        borderRadius: '20px',
                        padding: '40px',
                        maxWidth: '480px',
                        width: '100%',
                        textAlign: 'center',
                        backdropFilter: 'blur(12px)',
                    }}>
                        {/* Error icon */}
                        <div style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '16px',
                            background: 'rgba(239, 68, 68, 0.12)',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px',
                            fontSize: '24px',
                        }}>
                            <AlertTriangle size={24} color="#ef4444" />
                        </div>

                        <h2 style={{
                            margin: '0 0 8px',
                            fontSize: '18px',
                            fontWeight: 700,
                            color: '#eef1ff',
                            letterSpacing: '-0.01em',
                        }}>
                            {this.props.fallbackMessage || 'Error al cargar el módulo'}
                        </h2>

                        <p style={{
                            color: '#7b86b5',
                            margin: '0 0 24px',
                            fontSize: '13px',
                            lineHeight: '1.5',
                        }}>
                            {this.state.error?.message || 'Error desconocido'}
                        </p>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button
                                onClick={() => {
                                    this.setState({ hasError: false, error: null });
                                }}
                                style={{
                                    padding: '10px 20px',
                                    background: 'rgba(255, 255, 255, 0.06)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: '10px',
                                    color: '#d1d9ff',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    transition: 'all 0.2s',
                                }}
                            >
                                Reintentar
                            </button>
                            <button
                                onClick={() => {
                                    this.setState({ hasError: false, error: null });
                                    // Cache-bust para forzar index.html fresh
                                    const url = new URL(window.location.href);
                                    url.searchParams.set('cb', String(Date.now()));
                                    window.location.href = url.toString();
                                }}
                                style={{
                                    padding: '10px 20px',
                                    background: 'linear-gradient(135deg, #6f63ff 0%, #9f7aea 100%)',
                                    border: 'none',
                                    borderRadius: '10px',
                                    color: '#eef1ff',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    transition: 'all 0.2s',
                                }}
                            >
                                Recargar Página
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
